import React, { useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';
import { reduxForm, getFormValues } from 'redux-form';
import { useTranslation } from 'react-i18next';

import { DateField } from 'src/jbc-front/components/Form';
import CommonModal from 'jbc-front/components/CommonModal';
import Button from 'jbc-front/components/Button';
import { updatePaymentDate } from 'src/reducers/employees/employeePays';
import { getSelectOptions } from 'src/reducers/selectOptions';

const EditPaymentDateForm = ({
  isOpen,
  hideModal,
  pristine,
  submitting,
  handleSubmit,
  uniquePaymentDates,
  payrollRuleGroups,
  selectedPaymentDate,
  selectingPayrollRuleGroup
}) => {
  const { t, i18n } = useTranslation();
  const paymentMonthTypeEnum = {
    1: 'current_month',
    2: 'next_month'
  };

  const selectedPlanPaymentDate = moment(_.find(uniquePaymentDates, { value: selectedPaymentDate })?.planPaymentDate);

  useEffect(() => {
    moment.locale(i18n.language);
  }, []);

  const targetPaymentDateInValidRange = paymentDate => {
    const MAX_RANGE = 10;

    const { planPaymentDate } = paymentDate;
    const planPayDate = moment(planPaymentDate);
    const inBoundStartDate = planPayDate.clone().subtract(MAX_RANGE, 'days');
    const inBoundEndDate = planPayDate.clone().add(MAX_RANGE, 'days');

    return { inBoundStartDate, inBoundEndDate };
  };

  const calculateDateForCurrentMonth = useCallback(
    ({ paymentDate, inBoundEndDate, inBoundStartDate }) => {
      if (!paymentDate) return {};
      let minDate;
      let maxDate;

      const targetPaymentDate = moment(paymentDate.value, 'YYYY_MM_DD');
      const targetStartDate = moment(paymentDate.targetStartDate);
      const targetEndDate = moment(paymentDate.targetEndDate);
      const endOfMonth = targetPaymentDate.clone().endOf('month');

      if (targetPaymentDate > targetEndDate) {
        minDate = targetEndDate.clone().add(1, 'days');
        maxDate = inBoundEndDate.isBefore(endOfMonth) ? inBoundEndDate : endOfMonth;
      } else {
        minDate = inBoundStartDate.isAfter(targetStartDate) ? inBoundStartDate : targetStartDate;
        maxDate = inBoundEndDate.isBefore(targetEndDate) ? inBoundEndDate : targetEndDate;
      }
      return { maxDate, minDate };
    },
    [selectedPaymentDate]
  );

  const calculateDateForNextMonth = useCallback(
    ({ paymentDate, inBoundEndDate, inBoundStartDate }) => {
      if (!paymentDate) return {};
      const planPaymentDate = moment(paymentDate.planPaymentDate, 'YYYY_MM_DD');
      const startOfMonth = planPaymentDate.clone().startOf('month');
      const endOfMonth = planPaymentDate.clone().endOf('month');

      const minDate = inBoundStartDate.isAfter(startOfMonth) ? inBoundStartDate : startOfMonth;
      const maxDate = inBoundEndDate.isBefore(endOfMonth) ? inBoundEndDate : endOfMonth;
      return { maxDate, minDate };
    },
    [selectedPaymentDate, uniquePaymentDates, selectingPayrollRuleGroup]
  );

  const isValidDate = useCallback(
    currentDate => {
      const payrollRuleGroup = _.find(payrollRuleGroups, { value: selectingPayrollRuleGroup });
      const paymentDate = _.find(uniquePaymentDates, {
        planPaymentDate: selectedPlanPaymentDate.format('YYYY-MM-DD')
      });

      if (!payrollRuleGroup || !paymentDate) return true;

      const payType = paymentMonthTypeEnum[payrollRuleGroup.paymentMonthType];
      if (!payType) return true;

      const { inBoundEndDate, inBoundStartDate } = targetPaymentDateInValidRange(paymentDate);

      const calculateDateFn = payType === 'current_month' ? calculateDateForCurrentMonth : calculateDateForNextMonth;
      const { maxDate, minDate } = calculateDateFn({ paymentDate, inBoundStartDate, inBoundEndDate });

      if (!maxDate || !minDate) return true;

      if (currentDate.isBefore(minDate) || currentDate.isAfter(maxDate)) return false;

      return true;
    },
    [selectedPlanPaymentDate, uniquePaymentDates]
  );

  const memorizedIsValidDate = useMemo(() => isValidDate, [isValidDate]);

  return (
    <form>
      <CommonModal isOpen={isOpen} hideModal={hideModal}>
        <CommonModal.Header hideModal={hideModal}>{t('features.EmployeePays.EmployeePayList.edit_payment_date')}</CommonModal.Header>
        <CommonModal.Body style={{ height: '400px' }}>
          <DateField
            isValidDate={memorizedIsValidDate}
            label={t('features.EmployeePays.EmployeePayList.payment_day')}
            name="targetPaymentDate"
            viewDate={selectedPlanPaymentDate}
            required
          />
        </CommonModal.Body>
        <CommonModal.Footer>
          <CommonModal.Buttons>
            <Button onClick={hideModal}>{t('features.EmployeePays.EmployeePayList.cancel')}</Button>
            <Button primary disabled={pristine || submitting} onClick={handleSubmit}>
              {t('features.EmployeePays.EmployeePayList.save')}
            </Button>
          </CommonModal.Buttons>
        </CommonModal.Footer>
      </CommonModal>
    </form>
  );
};

const EditPaymentDate = reduxForm({
  form: 'editPaymentDate',
  enableReinitialize: true
})(EditPaymentDateForm);

const mapStateToProps = (state, ownProps) => ({
  uniquePaymentDates: getSelectOptions(state, 'uniquePaymentDates'),
  payrollRuleGroups: getSelectOptions(state, 'uniquePayrollRuleGroups'),
  selectingPayrollRuleGroup: getFormValues('paysSearchForm')(state).uniquePayrollRuleGroups,
  formValues: getFormValues('editPaymentDate')(state),
  initialValues: {
    targetPaymentDate: '',
    uniquePaymentDates: getSelectOptions(state, 'uniquePaymentDates'),
    selectedPaymentDate: ownProps.selectedPaymentDate
  }
});

const mapDispatchToProps = dispatch => ({
  onSubmit: data => dispatch(updatePaymentDate(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPaymentDate);
